// ============ FEATURES: Watchlist / Alerts / Onboarding / Settings ============
import React, { useState, useEffect, useRef } from 'react';
import { ASSETS, fmtNum, HD_TYPES } from '../data';
import { SecHead, Avatar } from '../components/core';
import { useT } from '../lib/i18n';
import { saveProfile, saveAvatar } from '../services/api';

const curOf = (a: any) => ({ SET: '฿', mai: '฿', BOND: '', JP: '¥', HK: 'HK$', EU: '€', IN: '₹', KR: '₩' } as any)[a.market] || '$';
// Interest tags are stored as Thai canonical values; this maps them to English for display only.
const INTEREST_EN: Record<string, string> = {
  'หุ้นไทย': 'Thai stocks', 'หุ้นต่างประเทศ': 'Global stocks', 'Crypto': 'Crypto', 'ทองคำ': 'Gold',
  'ETF / กองทุน': 'ETF / funds', 'หุ้นปันผล': 'Dividend stocks', 'Forex / MT5': 'Forex / MT5',
};

// ---- Topbar tools: star (watchlist), bell (alerts), gear (settings) ----
export function TopTools({ watchlist, toggleWatch, alerts, addAlert, removeAlert, onPick, openSettings, openHelp, user, isPremium, onAuth }: any) {
  const t = useT();
  const [open, setOpen] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const close = (e: any) => { if (ref.current && !ref.current.contains(e.target)) setOpen(null); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const watched = watchlist.map((s: string) => ASSETS.find(a => a.sym === s)).filter(Boolean) as any[];
  const triggered = alerts.filter((al: any) => {
    const a = ASSETS.find(x => x.sym === al.sym);
    return a && ((al.cond === 'above' && a.price >= al.price) || (al.cond === 'below' && a.price <= al.price));
  }).length;

  const [fSym, setFSym] = useState('NVDA');
  const [fCond, setFCond] = useState('above');
  const [fPrice, setFPrice] = useState('');
  const submitAlert = () => {
    const p = parseFloat(fPrice);
    if (!p) return;
    addAlert({ id: Date.now(), sym: fSym, cond: fCond, price: p });
    setFPrice('');
  };

  return (
    <div className="mm-toptools" ref={ref}>
      <button className={`mm-tool ${open === 'watch' ? 'on' : ''}`} title="Watchlist" onClick={() => setOpen(open === 'watch' ? null : 'watch')}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill={watched.length ? 'var(--gold)' : 'none'} stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"><path d="M12 2l2.9 6.3 6.9.8-5.1 4.7 1.4 6.8-6.1-3.4-6.1 3.4 1.4-6.8L2.2 9.1l6.9-.8z"></path></svg>
        {watched.length > 0 && <i className="mm-tool-badge mono">{watched.length}</i>}
      </button>
      <button className={`mm-tool ${open === 'alert' ? 'on' : ''}`} title={t('แจ้งเตือนราคา', 'Price alerts')} onClick={() => setOpen(open === 'alert' ? null : 'alert')}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 01-3.4 0"></path></svg>
        {triggered > 0 ? <i className="mm-tool-badge hot mono">{triggered}</i> : alerts.length > 0 ? <i className="mm-tool-badge mono">{alerts.length}</i> : null}
      </button>
      {openHelp && (
        <button className="mm-tool" title={t('ช่วยเหลือ / คู่มือ', 'Help / Guide')} onClick={() => { setOpen(null); openHelp(); }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M9.1 9a3 3 0 015.8 1c0 2-3 3-3 3"></path><line x1="12" y1="17" x2="12" y2="17"></line></svg>
        </button>
      )}
      <button className="mm-tool" title={t('ตั้งค่า / โปรไฟล์', 'Settings / Profile')} onClick={() => { setOpen(null); openSettings(); }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06a1.65 1.65 0 001.82.33h.01a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51h.01a1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82v.01a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"></path></svg>
      </button>

      {open === 'watch' && (
        <div className="mm-tooldrop glass">
          <div className="mm-tooldrop-h"><b>{t('Watchlist ส่วนตัว', 'Your Watchlist')}</b><span className="mono dim">{user ? `${watched.length}${isPremium ? '' : ' / 10'} ${t('รายการ', 'items')}` : t('สมาชิกเท่านั้น', 'Members only')}</span></div>
          {!user && (
            <div className="mm-tooldrop-empty">{t('สมัครฟรีเพื่อใช้ Watchlist ส่วนตัว', 'Sign up free to use your Watchlist')}<br />
              <button className="mm-btn primary sm" style={{ marginTop: 10 }} onMouseDown={() => onAuth('signup')}>{t('สมัครสมาชิกฟรี', 'Sign up free')}</button>
            </div>
          )}
          {user && watched.length === 0 && <div className="mm-tooldrop-empty">{t('ยังไม่มีหุ้นที่เก็บไว้', 'No saved assets yet')}<br /><span className="dim">{t('กดดาว ★ ในหน้าวิเคราะห์รายตัวเพื่อเก็บ', 'Tap ★ on the Analysis page to save')}</span></div>}
          {user && watched.map(a => (
            <div className="mm-watchrow" key={a.sym}>
              <button className="mm-watchrow-main" onMouseDown={() => { onPick(a.sym); setOpen(null); }}>
                <b className="mono">{a.sym}</b>
                <span className="dim">{a.name}</span>
                <span className="mono">{curOf(a)}{fmtNum(a.price, a.price > 1000 ? 0 : 2)}</span>
                <em className={`mono ${a.chg >= 0 ? 'up' : 'down'}`}>{a.chg >= 0 ? '+' : ''}{a.chg.toFixed(2)}%</em>
              </button>
              <button className="mm-watchrow-x" onClick={() => toggleWatch(a.sym)} title={t('เอาออก', 'Remove')}>★</button>
            </div>
          ))}
        </div>
      )}

      {open === 'alert' && (
        <div className="mm-tooldrop glass wide">
          <div className="mm-tooldrop-h"><b>{t('แจ้งเตือนราคา', 'Price alerts')}</b><span className="mono dim">{user ? `${alerts.length}${isPremium ? '' : ' / 5'} alert` : t('สมาชิกเท่านั้น', 'Members only')}</span></div>
          {!user ? (
            <div className="mm-tooldrop-empty">{t('สมัครฟรีเพื่อตั้งแจ้งเตือนราคา', 'Sign up free to set price alerts')}<br />
              <button className="mm-btn primary sm" style={{ marginTop: 10 }} onMouseDown={() => onAuth('signup')}>{t('สมัครสมาชิกฟรี', 'Sign up free')}</button>
            </div>
          ) : (
          <React.Fragment>
          <div className="mm-alertform">
            <select className="mm-ipo-select" value={fSym} onChange={e => setFSym(e.target.value)}>
              {ASSETS.map(a => <option key={a.sym} value={a.sym}>{a.sym}</option>)}
            </select>
            <select className="mm-ipo-select" value={fCond} onChange={e => setFCond(e.target.value)}>
              <option value="above">{t('สูงกว่า ≥', 'Above ≥')}</option>
              <option value="below">{t('ต่ำกว่า ≤', 'Below ≤')}</option>
            </select>
            <input className="mm-port-input sm" type="number" placeholder={t('ราคา', 'Price')} value={fPrice} onChange={e => setFPrice(e.target.value)} />
            <button className="mm-btn primary sm" onClick={submitAlert}>{t('ตั้ง', 'Set')}</button>
          </div>
          {alerts.length === 0 && <div className="mm-tooldrop-empty">{t('ยังไม่มีการแจ้งเตือน — ตั้งเงื่อนไขด้านบน', 'No alerts yet — set one above')}</div>}
          {alerts.map((al: any) => {
            const a = ASSETS.find(x => x.sym === al.sym);
            const hit = a && ((al.cond === 'above' && a.price >= al.price) || (al.cond === 'below' && a.price <= al.price));
            return (
              <div className={`mm-alertrow ${hit ? 'hit' : ''}`} key={al.id}>
                <span className={`mm-alert-dot ${hit ? 'hit' : ''}`}></span>
                <b className="mono">{al.sym}</b>
                <span className="dim">{al.cond === 'above' ? '≥' : '≤'} <i className="mono" style={{ fontStyle: 'normal' }}>{fmtNum(al.price, al.price > 1000 ? 0 : 2)}</i></span>
                <span className="mono dim">{t('ตอนนี้', 'now')} {a ? fmtNum(a.price, a.price > 1000 ? 0 : 2) : '—'}</span>
                {hit ? <em className="mm-alert-hit mono">{t('ถึงเป้า!', 'Hit!')}</em> : <em className="mono dim">{t('รอ', 'waiting')}</em>}
                <button className="mm-watchrow-x" onClick={() => removeAlert(al.id)}>✕</button>
              </div>
            );
          })}
          </React.Fragment>
          )}
        </div>
      )}
    </div>
  );
}

// ---- Onboarding modal (new members) ----
const OB_INTERESTS = ['หุ้นไทย', 'หุ้นต่างประเทศ', 'Crypto', 'ทองคำ', 'ETF / กองทุน', 'หุ้นปันผล', 'Forex / MT5'];
export function OnboardingModal({ user, setUser, onClose, onNav }: any) {
  const t = useT();
  const [step, setStep] = useState(0);
  const [picks, setPicks] = useState<string[]>(user?.interests || []);
  const togglePick = (p: string) => setPicks(x => x.includes(p) ? x.filter(i => i !== p) : [...x, p]);
  const finish = (goHD: boolean) => {
    if (user) setUser({ ...user, interests: picks });
    localStorage.setItem('mm_onboarded', '1');
    onClose();
    if (goHD) onNav('humandesign');
  };
  return (
    <div className="mm-modal-bg">
      <div className="mm-modal glass mm-ob" onClick={e => e.stopPropagation()}>
        <div className="mm-modal-orb"></div>
        <div className="mm-ob-steps mono">{[0, 1, 2].map(i => <i key={i} className={i <= step ? 'on' : ''}></i>)}</div>
        {step === 0 && (
          <React.Fragment>
            <img className="mm-ob-logo" src="/brand/logo-mark.png" alt="Master of the Market" />
            <h2>{t('ยินดีต้อนรับ ', 'Welcome ')}{user?.name || t('นักลงทุน', 'investor')} 🐯</h2>
            <p className="dim">{t('Master of the Market รวมการวิเคราะห์ Fundamental + Technical ทุกสินทรัพย์ทั่วโลก พร้อมถอดรหัสสไตล์นักลงทุนของคุณด้วยศาสตร์ Human Design', 'Master of the Market unites Fundamental + Technical analysis across global assets, and decodes your investor style with Human Design')}</p>
            <button className="mm-btn primary big w100" onClick={() => setStep(1)}>{t('เริ่มกันเลย →', "Let's go →")}</button>
          </React.Fragment>
        )}
        {step === 1 && (
          <React.Fragment>
            <h2>{t('คุณสนใจลงทุนอะไรบ้าง?', 'What are you interested in?')}</h2>
            <p className="dim">{t('เลือกได้หลายอย่าง — เราจะใช้ปรับเนื้อหาให้ตรงใจ', 'Pick several — we tailor content to you')}</p>
            <div className="mm-ob-picks">
              {OB_INTERESTS.map(p => (
                <button key={p} className={`mm-pill ${picks.includes(p) ? 'on' : ''}`} onClick={() => togglePick(p)}>{t(p, INTEREST_EN[p] || p)}</button>
              ))}
            </div>
            <button className="mm-btn primary big w100" onClick={() => setStep(2)} disabled={picks.length === 0}>{t('ถัดไป →', 'Next →')}</button>
          </React.Fragment>
        )}
        {step === 2 && (
          <React.Fragment>
            <h2>{t('ถอดรหัสนักลงทุนในตัวคุณ ✦', 'Decode the investor in you ✦')}</h2>
            <p className="dim">{t('ขั้นสุดท้าย — กรอกวันเวลาเกิดเพื่อเปิด Investor Bodygraph และรับพอร์ตแนะนำที่สอดคล้องกับพลังงานของคุณ', 'Last step — enter your birth date/time to unlock your Investor Bodygraph and a portfolio matched to your energy')}</p>
            <button className="mm-btn primary big w100" onClick={() => finish(true)}>{t('✦ ไปถอดรหัสเลย', '✦ Decode now')}</button>
            <button className="mm-btn ghost w100" style={{ marginTop: 8 }} onClick={() => finish(false)}>{t('ข้ามไปก่อน เริ่มใช้งานเลย', 'Skip for now, start using')}</button>
          </React.Fragment>
        )}
      </div>
    </div>
  );
}

// ---- Settings / Profile page ----
export function SettingsPage({ user, setUser, onAuth, onLogout, onNav }: any) {
  const t = useT();
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [saved, setSaved] = useState(false);
  const [picks, setPicks] = useState<string[]>(user?.interests || []);
  const prefs = user?.prefs || { alertPush: true, newsDigest: true, xdRemind: false };
  const togglePref = (k: string) => user && setUser({ ...user, prefs: { ...prefs, [k]: !prefs[k] } });
  const togglePick = (p: string) => {
    const nx = picks.includes(p) ? picks.filter(i => i !== p) : [...picks, p];
    setPicks(nx);
    if (user) setUser({ ...user, interests: nx });
  };
  // The session arrives after this page mounts, so the fields have to follow
  // it — they used to sit empty until the member typed something.
  useEffect(() => {
    if (!user) return;
    setName(user.name || '');
    setEmail(user.email || '');
    setPicks(user.interests || []);
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const [avaBusy, setAvaBusy] = useState(false);
  const [avaErr, setAvaErr] = useState('');
  const avaInput = useRef<HTMLInputElement>(null);
  const [saveErr, setSaveErr] = useState('');
  const save = async () => {
    if (!user) return;
    // Only the display name is editable: the email IS the login identity and
    // the account API has no path to change it. Claiming to save it (the old
    // behaviour) meant the header showed an address that vanished on reload.
    const nextName = name.trim() || user.name;
    setSaveErr('');
    const r = await saveProfile({ name: nextName }).catch(() => null);
    if (!r?.user) { setSaveErr(t('บันทึกไม่สำเร็จ — ลองใหม่อีกครั้ง', 'Save failed — please try again')); return; }
    setUser({ ...user, ...r.user });
    setSaved(true);
    setTimeout(() => setSaved(false), 1800);
  };
  const onAvatarFile = (file: File | null) => {
    if (!file) return;
    setAvaErr('');
    if (!/^image\/(jpeg|jpg|png|webp)$/i.test(file.type)) {
      setAvaErr(t('รองรับ JPG, PNG, WEBP', 'JPG, PNG or WEBP only')); return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setAvaErr(t('ไฟล์ใหญ่เกิน 2MB', 'File exceeds 2MB')); return;
    }
    setAvaBusy(true);
    const rd = new FileReader();
    rd.onload = async () => {
      const r = await saveAvatar(String(rd.result));
      setAvaBusy(false);
      if (r.user) setUser({ ...user, ...r.user });
      else setAvaErr(r.error === 'too_large' ? t('ไฟล์ใหญ่เกิน 2MB', 'File exceeds 2MB')
        : r.error === 'bad_type' ? t('รองรับ JPG, PNG, WEBP', 'JPG, PNG or WEBP only')
        : t('อัปโหลดไม่สำเร็จ', 'Upload failed'));
    };
    rd.onerror = () => { setAvaBusy(false); setAvaErr(t('อ่านไฟล์ไม่สำเร็จ', 'Could not read the file')); };
    rd.readAsDataURL(file);
  };
  const removeAvatar = async () => {
    setAvaBusy(true); setAvaErr('');
    const r = await saveAvatar(null);
    setAvaBusy(false);
    if (r.user) setUser({ ...user, ...r.user });
    else setAvaErr(t('ลบรูปไม่สำเร็จ', 'Could not remove the picture'));
  };
  if (!user) {
    return (
      <div className="mm-page" data-screen-label="Settings">
        <div className="mm-card glass mm-settings-guest">
          <h2>{t('ตั้งค่า / โปรไฟล์', 'Settings / Profile')}</h2>
          <p className="dim">{t('เข้าสู่ระบบเพื่อจัดการโปรไฟล์ ความสนใจ และการแจ้งเตือนของคุณ', 'Log in to manage your profile, interests and alerts')}</p>
          <div className="mm-port-actions">
            <button className="mm-btn primary big" onClick={() => onAuth('login')}>{t('เข้าสู่ระบบ', 'Log in')}</button>
            <button className="mm-btn ghost big" onClick={() => onAuth('signup')}>{t('สมัครสมาชิกฟรี', 'Sign up free')}</button>
          </div>
        </div>
      </div>
    );
  }
  const hd = user.hdType ? HD_TYPES[user.hdType] : null;
  return (
    <div className="mm-page" data-screen-label="Settings">
      <div className="mm-grid-2">
        <div className="mm-card glass">
          <SecHead th="โปรไฟล์" en="PROFILE"></SecHead>
          <div className="mm-settings-ava">
            <Avatar user={user} className="mm-ava xl"></Avatar>
            <div>
              <b>{user.name}</b>
              <span className="dim">{hd ? `${hd.name} · ${hd.th}` : t('ยังไม่ได้ถอดรหัส Human Design', 'Human Design not decoded yet')}</span>
              <span className="mm-ava-actions">
                <input ref={avaInput} type="file" accept="image/jpeg,image/png,image/webp" hidden
                  onChange={e => { onAvatarFile(e.target.files?.[0] || null); e.target.value = ''; }} />
                <button className="mm-btn ghost sm" disabled={avaBusy} onClick={() => avaInput.current?.click()}>
                  {avaBusy ? t('กำลังอัปโหลด…', 'Uploading…') : user.avatar ? t('เปลี่ยนรูป', 'Change photo') : t('📷 อัปโหลดรูป', '📷 Upload photo')}
                </button>
                {user.avatar && !avaBusy && (
                  <button className="mm-btn ghost sm" onClick={removeAvatar}>{t('ลบรูป', 'Remove')}</button>
                )}
              </span>
            </div>
          </div>
          {avaErr && <p style={{ color: 'var(--down, #ff5c7a)', margin: '0 0 8px' }}>{avaErr}</p>}
          <div className="mm-field"><label>{t('ชื่อที่ใช้แสดง', 'Display name')}</label><input type="text" value={name} onChange={e => setName(e.target.value)} /></div>
          <div className="mm-field">
            <label>{t('อีเมล (ใช้เข้าสู่ระบบ — เปลี่ยนเองไม่ได้)', 'Email (your login — cannot be changed here)')}</label>
            <input type="email" value={email} readOnly disabled />
          </div>
          {saveErr && <p style={{ color: 'var(--down, #ff5c7a)', margin: '0 0 8px' }}>{saveErr}</p>}
          <button className="mm-btn primary" onClick={save}>{saved ? t('✓ บันทึกแล้ว', '✓ Saved') : t('บันทึกโปรไฟล์', 'Save profile')}</button>
        </div>
        <div className="mm-col" style={{ gap: 18 }}>
          <div className="mm-card glass">
            <SecHead th="ความสนใจการลงทุน" en="INTERESTS"></SecHead>
            <div className="mm-filterrow" style={{ marginBottom: 0 }}>
              {OB_INTERESTS.map(p => (
                <button key={p} className={`mm-pill ${picks.includes(p) ? 'on' : ''}`} onClick={() => togglePick(p)}>{t(p, INTEREST_EN[p] || p)}</button>
              ))}
            </div>
          </div>
          <div className="mm-card glass">
            <SecHead th="การแจ้งเตือน" en="NOTIFICATIONS"></SecHead>
            {[['alertPush', t('แจ้งเตือนเมื่อราคาถึงเป้า', 'Alert when price hits target')], ['newsDigest', t('สรุปข่าวรายวันตอนเช้า', 'Morning daily news digest')], ['xdRemind', t('เตือนก่อนวันขึ้น XD หุ้นใน Watchlist', 'Remind before ex-dividend for watchlist')]].map(([k, label]) => (
              <label className="mm-setting-toggle" key={k}>
                <span>{label}</span>
                <input type="checkbox" checked={!!prefs[k]} onChange={() => togglePref(k)} />
                <i className="mm-switch"></i>
              </label>
            ))}
          </div>
          <div className="mm-card glass">
            <SecHead th="บัญชี" en="ACCOUNT"></SecHead>
            <div className="mm-port-actions">
              {!hd && <button className="mm-btn ghost" onClick={() => onNav('humandesign')}>{t('✦ ถอดรหัส Human Design', '✦ Decode Human Design')}</button>}
              <button className="mm-btn ghost" onClick={() => onNav('help')}>{t('📖 คู่มือการใช้งาน', '📖 User Guide')}</button>
              {user.isAdmin && <button className="mm-btn ghost" onClick={() => onNav('admin')}>{t('🔧 สถานะระบบ (Admin)', '🔧 System Status (Admin)')}</button>}
              <button className="mm-btn ghost danger" onClick={onLogout}>{t('ออกจากระบบ', 'Log out')}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
